import {useCallback} from 'react'

const STORAGE_KEY_PREFIX = 'hroc_checklist_draft_'
const DRAFT_EXPIRATION_DAYS = 7

type DraftWrapper<T> = {
  data: T
  timestamp: number
}

const getStorageKey = (prefix: string, id: string): string => `${prefix}${id}`

const isDraftExpired = (timestamp: number, expirationDays: number): boolean => {
  const daysSinceCreation = (Date.now() - timestamp) / (1000 * 60 * 60 * 24)
  return daysSinceCreation > expirationDays
}

export const useChecklistLocalStorage = <T>(checklistId: string) => {
  const storageKey = getStorageKey(STORAGE_KEY_PREFIX, checklistId)

  const saveDraft = useCallback(
    (values: T): void => {
      try {
        const wrapper: DraftWrapper<T> = {
          data: values,
          timestamp: Date.now()
        }
        localStorage.setItem(storageKey, JSON.stringify(wrapper))
      } catch (error) {
        console.error('Failed to save checklist draft to localStorage:', error)
      }
    },
    [storageKey]
  )

  const loadDraft = useCallback((): T | null => {
    try {
      const draft = localStorage.getItem(storageKey)
      if (!draft) return null

      const wrapper = JSON.parse(draft) as DraftWrapper<T>

      if (isDraftExpired(wrapper.timestamp, DRAFT_EXPIRATION_DAYS)) {
        console.warn(`Draft expired (>${DRAFT_EXPIRATION_DAYS} days old), ignoring and clearing`)
        localStorage.removeItem(storageKey)
        return null
      }

      return wrapper.data
    } catch (error) {
      console.error('Failed to load checklist draft from localStorage:', error)
      return null
    }
  }, [storageKey])

  const clearDraft = useCallback((): void => {
    try {
      localStorage.removeItem(storageKey)
    } catch (error) {
      console.error('Failed to clear checklist draft from localStorage:', error)
    }
  }, [storageKey])

  return {saveDraft, loadDraft, clearDraft}
}
